import { Users, Plus, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CountrySelector } from '@/components/ui/country-selector';
import type { FundEntityFormData } from './types';

interface FundEntitiesSectionProps {
  fundEntities: FundEntityFormData[];
  onAdd: () => void;
  onUpdate: (
    index: number,
    field: keyof FundEntityFormData,
    value: string
  ) => void;
  onRemove: (index: number) => void;
}

export function FundEntitiesSection({
  fundEntities,
  onAdd,
  onUpdate,
  onRemove,
}: FundEntitiesSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-700" />
            <CardTitle>Fund Entities</CardTitle>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAdd}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Entity
          </Button>
        </div>
        <CardDescription>
          Legal entities associated with this fund structure
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fundEntities.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <p className="text-gray-500 mb-4">No entities added yet.</p>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={onAdd}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add First Entity
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {fundEntities.map((entity, index) => (
              <div key={entity.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Entity {index + 1}</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`entity_name_${index}`}>Entity Name</Label>
                    <Input
                      id={`entity_name_${index}`}
                      value={entity.entity_name}
                      onChange={(e) =>
                        onUpdate(index, 'entity_name', e.target.value)
                      }
                      placeholder="DFF Capital GP B.V."
                    />
                  </div>
                  <div>
                    <Label htmlFor={`role_${index}`}>Role</Label>
                    <Select
                      value={entity.role}
                      onValueChange={(value) => onUpdate(index, 'role', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gp">General Partner (GP)</SelectItem>
                        <SelectItem value="manager">Fund Manager</SelectItem>
                        <SelectItem value="master_fund">Master Fund</SelectItem>
                        <SelectItem value="feeder_fund">Feeder Fund</SelectItem>
                        <SelectItem value="aiv">
                          Alternative Investment Vehicle (AIV)
                        </SelectItem>
                        <SelectItem value="holding_company">
                          Holding Company
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor={`jurisdiction_${index}`}>
                      Jurisdiction
                    </Label>
                    <CountrySelector
                      value={entity.jurisdiction}
                      onValueChange={(value) =>
                        onUpdate(index, 'jurisdiction', value)
                      }
                      placeholder="Select jurisdiction"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`registration_number_${index}`}>
                      Registration Number
                    </Label>
                    <Input
                      id={`registration_number_${index}`}
                      value={entity.registration_number}
                      onChange={(e) =>
                        onUpdate(index, 'registration_number', e.target.value)
                      }
                      placeholder="12345678"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`iban_${index}`}>IBAN</Label>
                    <Input
                      id={`iban_${index}`}
                      value={entity.iban}
                      onChange={(e) => onUpdate(index, 'iban', e.target.value)}
                      placeholder="NL42RABO0335679633"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
